import { useState } from 'react';
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client';
import {
  Container, Header, Segment, Button, Message, Form, Input, Checkbox, Divider, Table, Label, Icon,
} from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { PATHS } from '../../../../paths';

/**
 * Bringing a supporting organisation into Joyya.
 *
 * ---------------------------------------------------------------------------------------
 * TWO STEPS, BECAUSE THE MUTATIONS ARE TWO
 * ---------------------------------------------------------------------------------------
 *
 * Creating the organisation and inviting its people are separate calls, so a failure in the
 * second does not throw away the first. That is the whole reason the backend was split: a single
 * mutation taking a name and three addresses had no recovery from a typo.
 *
 * So the page is **resumable**. If the organisation was created and the invitations failed,
 * coming back lands straight on step two for that organisation rather than offering to create it
 * again — which would be the one thing that cannot work, since the name is now taken.
 */

const GATE = gql`
  query InviteGate {
    canCreateOrganisation
    streams { id name isDefault }
  }
`;

const CREATE_ORGANISATION = gql`
  mutation CreateSupportingOrganisation($name: String!, $streamIds: [Int!]!, $description: String) {
    createSupportingOrganisation(name: $name, streamIds: $streamIds, description: $description)
  }
`;

const INVITE = gql`
  mutation InviteToOrganisation($organisationId: Int!, $people: [InviteeInput!]!) {
    inviteToOrganisation(organisationId: $organisationId, people: $people) {
      email name role outcome invitationId inviteLink emailStatus
    }
  }
`;

const SUPPORTERS = gql`
  query SupportingOrganisationsForInvite {
    supportingOrganisationsForInvite {
      organisationId name supportingSince streamNames memberCount admins membershipManagers
      pendingInvitations { id email role expiresAt expired }
    }
  }
`;

interface SupporterSummary {
  organisationId: number;
  name: string;
  supportingSince: string;
  streamNames: string[];
  memberCount: number;
  admins: string[];
  membershipManagers: string[];
  pendingInvitations: { id: number; email: string; role: string | null; expiresAt: string; expired: boolean }[];
}

const INVITABLE_SEARCH = gql`
  query InvitablePeople($search: String!) {
    invitablePeople(search: $search) { userId name email organisationNames }
  }
`;

/** The three the setup form asks for. The mutation itself accepts any role the org defines. */
const ROWS: { role: string; label: string; hint: string; required: boolean }[] = [
  {
    role: 'PARTNER_ADMIN',
    label: 'Joyya administrator',
    hint: 'Manages their settings and decides who holds which role.',
    required: true,
  },
  {
    role: 'PARTNER_MEMBERSHIP_OFFICER',
    label: 'Membership manager',
    hint: 'Adds and removes their members.',
    required: true,
  },
  {
    role: 'PARTNER_LEADERSHIP',
    label: 'Leadership',
    hint: 'Writes updates and prayer requests. Optional, and you can add more later.',
    required: false,
  },
];

interface PersonRow {
  role: string;
  /** What was typed. Also the label when an existing person has been picked. */
  value: string;
  /** Set once an existing person is chosen from the suggestions. */
  userId: number | null;
}

const looksLikeAddress = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

/**
 * One person. **The address input leads and the search assists**, not the other way round.
 *
 * The people being invited to a brand-new organisation are, by definition, usually new — so the
 * search comes back empty most of the time. A search box with "add a new address" tucked behind it
 * would bury the common case behind the rare one.
 */
const PersonInput = ({ row, onChange, error }: {
  row: PersonRow;
  onChange: (next: PersonRow) => void;
  error: string | false;
}) => {
  const [suggest, { data }] = useLazyQuery(INVITABLE_SEARCH, { fetchPolicy: 'network-only' });
  const matches = (data?.invitablePeople ?? []) as any[];

  return (
    <div style={{ marginBottom: '0.5em' }}>
      <Input
        fluid
        placeholder="name@example.com"
        value={row.value}
        error={!!error}
        icon={row.userId ? 'user' : 'mail'}
        iconPosition="left"
        onChange={(e) => {
          const value = e.target.value;
          // Typing after choosing somebody clears the choice — otherwise the id would silently
          // win over the address on screen.
          onChange({ ...row, value, userId: null });
          if (value.trim().length >= 3) {
            suggest({ variables: { search: value.trim() } });
          }
        }}
      />

      {error && <div style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.2em' }}>{error}</div>}

      {row.userId == null && row.value.trim().length >= 3 && matches.length > 0 && (
        <div style={{ marginTop: '0.3em' }}>
          <span style={{ color: '#767676', fontSize: '0.9em' }}>Already on Joyya: </span>
          {matches.slice(0, 4).map((m) => (
            <Label
              as="a"
              key={m.userId}
              style={{ marginRight: '0.3em' }}
              onClick={() => onChange({ ...row, userId: m.userId, value: m.email ?? m.name })}
            >
              <Icon name="user" />
              {m.name || m.email}
              {m.organisationNames.length > 0 && (
                <Label.Detail>{m.organisationNames.join(', ')}</Label.Detail>
              )}
            </Label>
          ))}
        </div>
      )}

      {row.userId != null && (
        <div style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.2em' }}>
          Existing account — they'll be added and told, with no new password to set.
        </div>
      )}
    </div>
  );
};

const roleLabel = (role: string | null) => ROWS.find((r) => r.role === role)?.label ?? role ?? 'Member';

const shortDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Who already supports this organisation, and whether each one is actually up and running.
 *
 * "No administrator yet" is the line this exists for: an organisation created here whose
 * invitations then failed is otherwise invisible — nothing errors, its people just never arrive.
 * "Invite people" goes to step two for that organisation, which is what makes the page resumable.
 */
const SupportersList = ({ supporters, onInvite }: {
  supporters: SupporterSummary[];
  onInvite: (s: SupporterSummary) => void;
}) => (
  <>
    <Header as="h2">
      Supporting this organisation
      <Header.Subheader>
        {supporters.length === 0
          ? 'None yet.'
          : `${supporters.length} organisation${supporters.length === 1 ? '' : 's'}. Invite more of their people, or finish setting one up.`}
      </Header.Subheader>
    </Header>

    {supporters.length > 0 && (
      <Table compact>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Organisation</Table.HeaderCell>
            <Table.HeaderCell>Runs it</Table.HeaderCell>
            <Table.HeaderCell>Invitations</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {supporters.map((s) => {
            const live = s.pendingInvitations.filter((i) => !i.expired);
            const expired = s.pendingInvitations.filter((i) => i.expired);
            return (
              <Table.Row key={s.organisationId} warning={s.admins.length === 0}>
                <Table.Cell>
                  <strong>{s.name}</strong>
                  <div style={{ color: '#767676', fontSize: '0.9em' }}>
                    {s.memberCount} member{s.memberCount === 1 ? '' : 's'} · since {shortDate(s.supportingSince)}
                  </div>
                  <div style={{ marginTop: '0.3em' }}>
                    {s.streamNames.length > 0
                      ? s.streamNames.map((n) => <Label key={n} size="tiny" style={{ marginBottom: '0.2em' }}>{n}</Label>)
                      : <span style={{ color: '#9f3a38', fontSize: '0.9em' }}>Reads no streams</span>}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  {s.admins.length > 0
                    ? <div><span style={{ color: '#767676' }}>Admin:</span> {s.admins.join(', ')}</div>
                    : <div style={{ color: '#9f3a38' }}><Icon name="warning sign" />No administrator yet</div>}
                  {s.membershipManagers.length > 0
                    ? <div><span style={{ color: '#767676' }}>Membership:</span> {s.membershipManagers.join(', ')}</div>
                    : <div style={{ color: '#767676' }}>No membership manager</div>}
                </Table.Cell>
                <Table.Cell>
                  {s.pendingInvitations.length === 0 && <span style={{ color: '#767676' }}>None outstanding</span>}
                  {live.map((i) => (
                    <div key={i.id}>{i.email} <span style={{ color: '#767676' }}>({roleLabel(i.role)}, until {shortDate(i.expiresAt)})</span></div>
                  ))}
                  {expired.map((i) => (
                    <div key={i.id} style={{ color: '#9f3a38' }}>{i.email} <span>({roleLabel(i.role)}, expired)</span></div>
                  ))}
                </Table.Cell>
                <Table.Cell collapsing>
                  <Button size="small" onClick={() => onInvite(s)}>Invite people</Button>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    )}
  </>
);

export const InviteSupportingOrganisation = () => {
  const { data: gate, loading: gateLoading } = useQuery(GATE);
  // Fresh every visit: the point is to see what changed since the last invitation went out.
  const { data: supportersData } = useQuery(SUPPORTERS, { fetchPolicy: 'network-only' });

  const [name, setName] = useState('');
  const [streamIds, setStreamIds] = useState<number[]>([]);
  const [organisationId, setOrganisationId] = useState<number | null>(null);
  const [createdName, setCreatedName] = useState('');
  // True when step two was reached from the list rather than by creating the organisation just now.
  const [existing, setExisting] = useState(false);

  const [people, setPeople] = useState<PersonRow[]>(
    ROWS.map((r) => ({ role: r.role, value: '', userId: null })),
  );
  const [showErrors, setShowErrors] = useState(false);
  const [results, setResults] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createOrganisation, { loading: creating }] = useMutation(CREATE_ORGANISATION);
  const [invite, { loading: inviting }] = useMutation(INVITE);

  if (gateLoading) return null;

  // A page that shows buttons the server will refuse is worse than no page — the same gate the
  // roles page uses, for the same reason.
  if (!gate?.canCreateOrganisation) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const streams = (gate?.streams ?? []) as { id: number; name: string; isDefault: boolean }[];

  // A new organisation needs its administrator and membership manager. An existing one may
  // already have both, so there it is "at least one person", checked in handleInvite.
  const isRequired = (role: string) => !existing && ROWS.find((r) => r.role === role)!.required;

  const errorFor = (index: number): string | false => {
    if (!showErrors) return false;
    const row = people[index];
    const spec = ROWS.find((r) => r.role === row.role)!;
    const value = row.value.trim();

    if (value === '') {
      return isRequired(row.role) ? `${spec.label} is required` : false;
    }
    if (row.userId == null && !looksLikeAddress(value)) {
      return 'That does not look like an email address';
    }
    // The same person twice in one role is a mistake; across two roles it is deliberate and fine.
    const duplicate = people.some((o, i) =>
      i !== index && o.role === row.role
      && (row.userId != null ? o.userId === row.userId : o.value.trim().toLowerCase() === value.toLowerCase()));

    return duplicate ? 'Already listed for this role' : false;
  };

  const handleCreate = async () => {
    setShowErrors(true);
    setError(null);

    if (name.trim() === '' || streamIds.length === 0) return;

    try {
      const res = await createOrganisation({
        variables: { name: name.trim(), streamIds, description: null },
      });
      setOrganisationId(res.data.createSupportingOrganisation);
      setCreatedName(name.trim());
      setShowErrors(false);
    } catch (e: any) {
      setError(e?.message ?? 'Could not create that organisation.');
    }
  };

  const handleInvite = async () => {
    setShowErrors(true);
    setError(null);

    const filled = people.filter((p) => p.value.trim() !== '');
    if (people.some((_, i) => errorFor(i) !== false)) return;
    if (ROWS.filter((r) => isRequired(r.role)).some((r) => !filled.some((p) => p.role === r.role))) return;
    if (filled.length === 0) {
      setError('Add at least one person.');
      return;
    }

    try {
      const res = await invite({
        variables: {
          organisationId,
          people: filled.map((p) => p.userId != null
            ? { role: p.role, userId: p.userId }
            : { role: p.role, email: p.value.trim() }),
        },
      });
      setResults(res.data.inviteToOrganisation);
    } catch (e: any) {
      setError(e?.message ?? 'Could not invite those people.');
    }
  };

  const deliveryLabel = (status: string) => ({
    ACCEPTED: 'Accepted for delivery',
    REJECTED: 'Rejected by the mail server',
    NOT_CONFIGURED: 'Email is not configured here',
    FAKED_IN_DEV: 'Faked — development',
    NO_ADDRESS: 'No address on file',
  } as Record<string, string>)[status] ?? status;

  const startInvite = (s: SupporterSummary) => {
    setOrganisationId(s.organisationId);
    setCreatedName(s.name);
    setExisting(true);
    setPeople(ROWS.map((r) => ({ role: r.role, value: '', userId: null })));
    setShowErrors(false);
    setError(null);
    setResults(null);
  };

  const backToList = () => {
    setOrganisationId(null);
    setCreatedName('');
    setExisting(false);
    setName('');
    setStreamIds([]);
    setPeople(ROWS.map((r) => ({ role: r.role, value: '', userId: null })));
    setShowErrors(false);
    setError(null);
    setResults(null);
  };

  const supporters = (supportersData?.supportingOrganisationsForInvite ?? []) as SupporterSummary[];

  // ── Results ────────────────────────────────────────────────────────────────
  if (results) {
    return (
      <ResponsiveContainer>
        <Container style={{ paddingTop: '2em', maxWidth: '760px' }}>
          <Header as="h1">{createdName}</Header>
          {existing
            ? <Message positive header="Invitations sent" content="Their people are below." />
            : (
              <Message
                positive
                header="Organisation created"
                content="They can see the streams you chose. Their people are below."
              />
            )}

          <Table compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Person</Table.HeaderCell>
                <Table.HeaderCell>Role</Table.HeaderCell>
                <Table.HeaderCell>What happened</Table.HeaderCell>
                <Table.HeaderCell>Email</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {results.map((r, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{r.name || r.email}</Table.Cell>
                  <Table.Cell>{ROWS.find((x) => x.role === r.role)?.label ?? r.role}</Table.Cell>
                  <Table.Cell>
                    {r.outcome === 'INVITED' ? 'Invited'
                      : r.outcome === 'ALREADY_MEMBER' ? 'Already a member'
                        : 'Added to the organisation'}
                  </Table.Cell>
                  {/* "Accepted for delivery", never "Sent" — SES accepting a message is not the
                      same as delivering it, and a bounce can arrive minutes later. */}
                  <Table.Cell>{deliveryLabel(r.emailStatus)}</Table.Cell>
                  <Table.Cell collapsing>
                    {r.inviteLink && (
                      <Button
                        size="tiny"
                        icon="copy"
                        content="Copy link"
                        onClick={() => navigator.clipboard?.writeText(r.inviteLink)}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <p style={{ color: '#767676' }}>
            An invitation expires in 7 days. If an address was wrong, you can invite that person
            again from this organisation — nothing here has to be undone first.
          </p>

          {/* A full page load, so the list refetches and shows these invitations as pending. */}
          <Button as="a" href={PATHS.ADMIN_SUPPORTER_INVITE}>Back to supporting organisations</Button>
          <Button as="a" href={PATHS.ADMIN_SETTINGS}>Done</Button>
        </Container>
      </ResponsiveContainer>
    );
  }

  // ── Step two: the people ───────────────────────────────────────────────────
  if (organisationId != null) {
    return (
      <ResponsiveContainer>
        <Container style={{ paddingTop: '2em', maxWidth: '640px' }}>
          <Header as="h1">
            {createdName}
            <Header.Subheader>
              {existing
                ? 'Invite more of their people. Fill in only the roles you need.'
                : 'Created. Now invite the people who will run it.'}
            </Header.Subheader>
          </Header>

          <Form error={!!error}>
            {ROWS.map((spec, i) => (
              <Segment key={spec.role}>
                <Header as="h4" style={{ marginBottom: '0.2em' }}>
                  {spec.label}
                  {!isRequired(spec.role) && <span style={{ color: '#767676', fontWeight: 400 }}> — optional</span>}
                </Header>
                <p style={{ color: '#767676', marginBottom: '0.6em' }}>{spec.hint}</p>
                <PersonInput
                  row={people[i]}
                  error={errorFor(i)}
                  onChange={(next) => setPeople(people.map((p, j) => (j === i ? next : p)))}
                />
              </Segment>
            ))}

            {error && <Message error header="Could not invite those people" content={error} />}

            <Button primary loading={inviting} disabled={inviting} onClick={handleInvite}>
              Send invitations
            </Button>
            {/* Only from the list: a just-created organisation going "back" would offer to create
                it again, which cannot work now that the name is taken. */}
            {existing && <Button basic onClick={backToList}>Back</Button>}
          </Form>
        </Container>
      </ResponsiveContainer>
    );
  }

  // ── Step one: the organisation ─────────────────────────────────────────────
  // Wider than the form, so the list below has room; the form keeps its narrow column.
  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '960px' }}>
        <div style={{ maxWidth: '640px' }}>
        <Header as="h1">
          Invite a supporting organisation
          <Header.Subheader>
            A church or partner organisation that supports this one. You create it here, then
            invite the people who will run it.
          </Header.Subheader>
        </Header>

        <Form error={!!error}>
          <Form.Field>
            <label>Organisation name</label>
            <Input
              value={name}
              placeholder="South West Baptist Church"
              error={showErrors && name.trim() === ''}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Field>

          <Form.Field>
            <label>What can they read?</label>
            <p style={{ color: '#767676', marginTop: 0 }}>
              Everyone in that organisation will see these streams, including people who join
              later.
            </p>
            {streams.map((s) => (
              <div key={s.id} style={{ marginBottom: '0.4em' }}>
                <Checkbox
                  label={s.isDefault ? `${s.name} (default)` : s.name}
                  checked={streamIds.includes(s.id)}
                  onChange={(_, d) => setStreamIds(d.checked
                    ? [...streamIds, s.id]
                    : streamIds.filter((x) => x !== s.id))}
                />
              </div>
            ))}
            {/* Required, not advised. An organisation created with no streams is invisible to its
                own people on the day they are invited, which is the worst moment for it. */}
            {showErrors && streamIds.length === 0 && (
              <div style={{ color: '#9f3a38', fontSize: '0.9em' }}>
                Choose at least one, or they will sign in and see nothing.
              </div>
            )}
          </Form.Field>

          {error && <Message error header="Could not create that organisation" content={error} />}

          <Divider />

          <Button primary loading={creating} disabled={creating} onClick={handleCreate}>
            Create organisation
          </Button>
        </Form>
        </div>

        <Divider section />

        <SupportersList supporters={supporters} onInvite={startInvite} />
      </Container>
    </ResponsiveContainer>
  );
};
